import { Injectable, WritableSignal } from "@angular/core";
import { Location } from "../types/Location/Location";
import { RequestService } from "./request.service";
import { LocationHistory } from "../types/Location/LocationHistory";
import { IrefTabsRepository } from "../repositories/iref.tabs.repository";
import { BaseService } from "./base.service";
import { LogService } from "./log.service";

@Injectable({
  providedIn: "root",
})
export class HistoryService extends BaseService {
  constructor(
    private requestService: RequestService,
    private tabsRepo: IrefTabsRepository,
    logService: LogService,
  ) {
    super(logService, "HistoryService");
  }

  private addReloadDataToHistoryMap(
    historyMap: { [locationID: string]: any },
    reloadData: any,
  ) {
    const historyMapWithReloadData: { [locationID: string]: any } =
      Object.entries(historyMap).reduce(
        (acc, [locationID, value]: [string, any]) => ({
          ...acc,
          [locationID]: Object.entries(value).reduce(
            (acc2, [inspectionID, value2]: [string, any]) => ({
              ...acc2,
              [inspectionID]: {
                ...value2,
                reloadData: reloadData[inspectionID],
              },
            }),
            {},
          ),
        }),
        {},
      );
    return historyMapWithReloadData;
  }

  public async downloadHistory(
    loadingMessage: WritableSignal<string>,
    records: Location[],
  ): Promise<{ [inspectionID: string]: LocationHistory }> {
    loadingMessage.set("Downloading History...");

    const qHist: any = await this.requestService.post("history/v2", {
      locations: records
        ? records.map((record) => ({
            locationID: record.locationID,
            parentFormID: record.parentFormID,
          }))
        : [],
    });

    if (qHist?.results) {
      const reloadData = await this.downloadReloadData(
        qHist.results,
        loadingMessage,
      );
      const res = this.addReloadDataToHistoryMap(qHist.results, reloadData);
      return res;
    }
    return {};
  }

  private handleReloads(res: any) {
    const filtered = res.filter((p: any) => p).flat();
    return filtered.reduce((acc: any, item: any) => {
      if (!acc[item.parentID]) {
        return { ...acc, [item.parentID]: { [item.tabID]: [item] } };
      }

      if (acc[item.parentID] && !acc[item.parentID][item.tabID]) {
        return {
          ...acc,
          [item.parentID]: {
            ...acc[item.parentID],
            [item.tabID]: [item],
          },
        };
      }
      return {
        ...acc,
        [item.parentID]: {
          ...acc[item.parentID],
          [item.tabID]: [...acc[item.parentID][item.tabID], item],
        },
      };
    }, {});
  }

  private getReloadsToFetch(history: any): any[] {
    const allHistory = history.flatMap((record: any) => {
      return Object.values(record);
    });
    const tabMap: { [key: string]: any } = allHistory.reduce(
      (acc: any, item: any) => {
        if (acc[item.type]) {
          return {
            ...acc,
            [item.type]: {
              ...acc[item.type],
              historyInspectionIDs: [
                ...acc[item.type].historyInspectionIDs,
                item.id,
              ],
            },
          };
        }
        const tabs = this.tabsRepo.getIrefTabsByInspectionTypeID(item.type);
        const transformedTabs = tabs.map((tab: any) => {
          return {
            tableName: tab.srcData,
            tabID: tab.tabID,
            copyReloadData: tab.tabData?.copyReloadData,
          };
        });
        return {
          ...acc,
          [item.type]: {
            tabs: transformedTabs,
            historyInspectionIDs: [item.id],
          },
        };
      },
      {},
    );
    return Object.values(tabMap).reduce((acc: any, item: any) => {
      const tabsToCopy = item.tabs.filter((tab: any) => tab.copyReloadData);
      if (tabsToCopy.length) {
        const tabsToCopyWithInspectionIDs = tabsToCopy.map((tab: any) => ({
          ...tab,
          historyInspectionIDs: item.historyInspectionIDs,
        }));
        return [...acc, ...tabsToCopyWithInspectionIDs];
      }
      return [...acc];
    }, [] as any[]);
  }

  private async fetchReloadData(tabsToFetch: any[]) {
    const promises = await Promise.all(
      tabsToFetch.map(async (tab: any) => {
        const res: any = await this.requestService.get("schemas/download", {
          tableName: tab.tableName,
          where: JSON.stringify([
            {
              comparisonOperator: "IN",
              logicalOperator: "AND",
              value: tab.historyInspectionIDs,
              column: "parentID",
              type: "listguid",
            },
          ]),
        });
        if (res.response === "Error") {
          throw new Error("Error Downloading Reloads");
        }
        if (res && res.response === "Success" && res?.data?.DATA) {
          return res.data.DATA.map((d: any) => ({ ...d, tabID: tab.tabID }));
        }
        this.logger.error("Error Downloading Reload Data", res);
        return false;
      }),
    );
    return this.handleReloads(promises);
  }

  private async downloadReloadData(
    historyMap: LocationHistory,
    loadingMessage: WritableSignal<string>,
  ): Promise<any> {
    const history = Object.values(historyMap);
    if (history && history.length) {
      loadingMessage.set("Downloading Reload Data...");
      const tabsToFetch = this.getReloadsToFetch(history);
      const reloadData = await this.fetchReloadData(tabsToFetch);
      return reloadData;
    }
    return null;
  }
}
